// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/qd-chart.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// qd-chart.ts — QUARTERDECK's ground: "Lantern over the chart."
//
// A field of hairline RHUMB LINES radiating from an off-canvas centre,
// engraved into the ground and PERFECTLY STILL, with a single soft pool of
// warm light drifting slowly across it on a pendulum arc, brightening
// whichever lines it grazes.
//
// THE SPLIT IS THE SIGNATURE (cross-style rulings R2.2 / R3 / R12). The
// geometry NEVER ROTATES — a rotating radial sweep is Destroyer's,
// exclusively — and the light NEVER BLURS ANYTHING — Glass's lights are cold,
// plural, theme-tinted and sit BEHIND the material, where they are refracted.
// QUARTERDECK's is ONE warm source ABOVE the surface, raking across engraved
// lines and revealing them. Refraction and raking light are opposite optical
// events, which is what makes the two unmistakable at thumbnail size.
//
// IT OWNS NO CANVAS. PR-4's style-axis.ts owns the element, the ONE-canvas
// cap, the rAF loop and pause-on-hidden; this module registers a PAINTER
// (`(ctx, t) => void`) and is called once per frame with the substrate's
// clock. That split is why scripts/perf-budget.mjs can prove the cap and the
// pause by driving the substrate — a style running its own loop would be
// outside every one of those arms. It is also why the two parents of this
// design are FUSED into one canvas rather than layered as two: the ceiling is
// one, and the rhumb field is what gives the lantern something worth lighting.
//
// TWO BUFFERS, ONE LOOP — the reason this costs almost nothing:
//   BUFFER A  the 32-point rhumb field plus three concentric distance arcs,
//             painted ONCE to an offscreen buffer and repainted only on a
//             resize or a theme change. The disciplined parent attempt spent
//             a whole rAF loop on a 6% opacity oscillation, which is a CSS
//             keyframe; this spends it on geometry that is worth a canvas.
//   BUFFER B  one createRadialGradient pool, composited with
//             `globalCompositeOperation = 'lighter'` so it ADDS light to the
//             lines it crosses instead of covering them.
// Per frame: clearRect -> drawImage(A) -> one gradient fill. TWO composites,
// independent of line count.
//
// WHY IT NEVER RUNS UNDER A REDUCE LANE: style-axis.ts's effectiveGrade()
// degrades `canvas` to `gradient` whenever prefers-reduced-motion OR
// prefers-reduced-transparency matches, so the element is never constructed
// and this painter is never called. styles.css paints the same rhumb field
// and one frozen lantern pool at the swing's midpoint as a static
// background-image instead — the still frame.

/** The pendulum period, ms. A hanging lamp, not a metronome: slow enough
 * that a reader never catches it moving, which is the acceptance criterion
 * the ambient-motion ruling (R2.3) states for every style in the programme. */
export const LANTERN_PERIOD_MS = 14_000;

/** Hard alpha ceilings, mirrored here so a stylesheet that loses a token
 * cannot silently raise them. `--qd-rhumb-alpha` / `--qd-lantern-alpha` are
 * the live values; these are what they are clamped to, and they are the same
 * numbers the CSS still frame paints. */
export const RHUMB_ALPHA_CEILING = 0.035;
export const LANTERN_ALPHA_CEILING = 0.055;

/** Points on the rose. 32 is the compass, not a round number. */
export const RHUMB_POINTS = 32;

/** The three concentric distance arcs, as fractions of the longer viewport
 * edge, measured from the off-canvas centre. */
const ARC_RADII = [0.45, 0.75, 1.05];

/** The rhumb centre, in viewport fractions. Off-canvas to the lower left, so
 * the field reads as one corner of a much larger chart. */
const CENTRE = { x: -0.15, y: 1.18 };

/** The lantern's rest point and swing amplitude, in viewport fractions. The
 * vertical term is |cos| at a sixth of the horizontal amplitude — the small
 * rise of a lamp on a hook at each end of its arc. */
const LANTERN = { cx: 0.5, cy: 0.42, amp: 0.26, rise: 1 / 6, radius: 0.38 };

/** How often layout and theme tokens are re-read (ms). Both are
 * getComputedStyle / clientWidth reads: cheap, but not 60 times a second. A
 * resize or a theme switch lands within one window, and the rhumb buffer is
 * repainted only when something it depends on actually moved. */
const REFRESH_MS = 500;

/** dpr is capped: a 3x backing store for hairlines and one soft gradient is
 * pure cost. */
const MAX_DPR = 2;

interface Env {
  at: number;
  w: number;
  h: number;
  dpr: number;
  rhumb: number;
  lantern: number;
  accent: string;
  accent2: string;
}

type Buffer = { canvas: HTMLCanvasElement | OffscreenCanvas; key: string };

let env: Env | null = null;
let buffer: Buffer | null = null;

/** `#rrggbb` + alpha -> an `rgba()` the 2D context can take. A value this
 * cannot parse (a colour name, an `rgb()`) is handed through unchanged rather
 * than guessed at. */
export function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const h = m?.[1];
  if (!h) return color;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function readEnv(canvas: HTMLCanvasElement, now: number): Env {
  const cs = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string): string => cs.getPropertyValue(name).trim() || fallback;
  const num = (name: string, ceiling: number): number => {
    const v = parseFloat(tok(name, String(ceiling)));
    return Number.isFinite(v) ? Math.min(Math.max(v, 0), ceiling) : ceiling;
  };
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, MAX_DPR);
  const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
  if (canvas.width !== want.w || canvas.height !== want.h) {
    canvas.width = want.w;
    canvas.height = want.h;
  }
  return {
    at: now,
    w,
    h,
    dpr,
    rhumb: num('--qd-rhumb-alpha', RHUMB_ALPHA_CEILING),
    lantern: num('--qd-lantern-alpha', LANTERN_ALPHA_CEILING),
    accent: tok('--accent', '#C99A45'),
    accent2: tok('--accent-2', '#E2B662'),
  };
}

/** Paint BUFFER A. Everything in it is static, so it is keyed on the only
 * four things that can change it — size, dpr, the accent, the alpha — and
 * rebuilt only when that key moves. */
function buildRhumbField(e: Env): Buffer {
  const key = `${e.w}x${e.h}@${e.dpr}/${e.accent}/${e.rhumb}`;
  if (buffer && buffer.key === key) return buffer;
  const W = Math.max(1, Math.round(e.w * e.dpr));
  const H = Math.max(1, Math.round(e.h * e.dpr));
  const surface: HTMLCanvasElement | OffscreenCanvas =
    typeof OffscreenCanvas === 'function'
      ? new OffscreenCanvas(W, H)
      : Object.assign(document.createElement('canvas'), { width: W, height: H });
  const g = surface.getContext('2d') as CanvasRenderingContext2D | null;
  if (g) {
    g.setTransform(e.dpr, 0, 0, e.dpr, 0, 0);
    g.strokeStyle = withAlpha(e.accent, e.rhumb);
    g.lineWidth = 1;
    const ox = CENTRE.x * e.w;
    const oy = CENTRE.y * e.h;
    const len = Math.hypot(e.w, e.h) * 2.2;
    for (let i = 0; i < RHUMB_POINTS; i++) {
      const a = (i / RHUMB_POINTS) * Math.PI * 2;
      g.beginPath();
      g.moveTo(ox, oy);
      g.lineTo(ox + len * Math.cos(a), oy + len * Math.sin(a));
      g.stroke();
    }
    for (const k of ARC_RADII) {
      g.beginPath();
      g.arc(ox, oy, k * Math.max(e.w, e.h), 0, Math.PI * 2);
      g.stroke();
    }
  }
  buffer = { canvas: surface, key };
  return buffer;
}

/**
 * THE painter. Registered against the QUARTERDECK style id; the substrate
 * calls it once per animation frame with a monotonic clock.
 */
export function paintQuarterdeckChart(ctx: CanvasRenderingContext2D, t: number): void {
  const canvas = ctx.canvas as HTMLCanvasElement;
  if (!env || t - env.at > REFRESH_MS || t < env.at) env = readEnv(canvas, t);
  const e = env;
  const field = buildRhumbField(e);

  ctx.setTransform(e.dpr, 0, 0, e.dpr, 0, 0);
  ctx.clearRect(0, 0, e.w, e.h);
  // The buffer is already at device resolution; draw it in CSS units so the
  // one transform above governs both layers.
  ctx.drawImage(field.canvas as CanvasImageSource, 0, 0, e.w, e.h);

  // ---- the lantern: ONE warm pool on a pendulum arc --------------------
  const p = ((t % LANTERN_PERIOD_MS) / LANTERN_PERIOD_MS) * Math.PI * 2;
  const A = LANTERN.amp * e.w;
  const x = LANTERN.cx * e.w + A * Math.sin(p);
  const y = LANTERN.cy * e.h + A * LANTERN.rise * Math.abs(Math.cos(p));
  const r = LANTERN.radius * Math.min(e.w, e.h);
  const pool = ctx.createRadialGradient(x, y, 0, x, y, r);
  pool.addColorStop(0, withAlpha(e.accent2, e.lantern));
  pool.addColorStop(1, withAlpha(e.accent2, 0));
  // 'lighter' is the whole reason the light READS as light: it adds to the
  // hairlines it crosses rather than covering them, which is what "raking
  // light reveals engraved linework" means in a compositor.
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = pool;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
  ctx.globalCompositeOperation = 'source-over';
}

/** Test seam — the gate and the smoke drive the painter across arms. */
export function resetQuarterdeckChart(): void {
  env = null;
  buffer = null;
}
